<template>
  <section>
    <div v-show="isValidToken && !isLoading" class="hero">
      <div class="hero-body container box has-background-white-bis">
        <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage" id="error-container"></error-display-component>
        <div v-if="creditApplicationCompleted" class="credit-app is-success is-flex">
          <div>
            <i class="fas fa-check-circle is-success fa-2x"></i>
          </div>
          <div class="ml-20 has-text-left">
            <span class="is-size-5 has-text-weight-semibold">Application Completed!</span><br />
            Your credit application has already been completed and submitted for processing.
          </div>
        </div>
        <div class="steps" id="stepsDemo">
          <div class="step-item is-active is-success">
            <div class="step-marker">1</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Business" class="fal fa-building fa-lg"></i>
              </p>
              <p class="step-title" v-else>BUSINESS INFO</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">2</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Residence" class="fal fa-home-alt fa-lg"></i>
              </p>
              <p class="step-title" v-else>BUSINESS ADDRESS</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">3</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Customer" class="fal fa-user fa-lg"></i>
              </p>
              <p class="step-title" v-else>CUSTOMER INFO</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">4</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Residence" class="fal fa-home-alt fa-lg"></i>
              </p>
              <p class="step-title" v-else>RESIDENCE</p>
            </div>
          </div>
          <div v-show="showWorkStep" class="step-item">
            <div class="step-marker">{{ workHistoryStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Work" class="fal fa-building fa-lg"></i>
              </p>
              <p class="step-title" v-else>WORK</p>
            </div>
          </div>
          <div v-show="showSpouseStep" class="step-item">
            <div class="step-marker">{{ spouseInfoStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
              </p>
              <p class="step-title" v-else>SPOUSE</p>
            </div>
          </div>
          <div v-show="showSpouseResidence" class="step-item">
            <div class="step-marker">{{ spouseInfoStepCount + 1 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none; display: inline-block; white-space: nowrap;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
                <i title="Residence" class="fal fa-home-alt fa-sx"></i>
              </p>
              <p class="step-title" v-else>SPOUSE RESIDENCE</p>
            </div>
          </div>
          <div v-show="showSpouseWork" class="step-item">
            <div class="step-marker">{{ spouseWorkStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none; display: inline-block; white-space: nowrap;" v-if="mobile">
                <i title="Spouse" class="fal fa-user-friends fa-lg"></i>
                <i title="Work" class="fal fa-building fa-sx"></i>
              </p>
              <p class="step-title" v-else>SPOUSE WORK</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">{{ bankInfoStepCount }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Work" class="fal fa-building fa-lg"></i>
              </p>
              <p class="step-title" v-else>BANK INFO</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-marker">{{ bankInfoStepCount + 1 }}</div>
            <div class="step-details">
              <p class="step-title" style="text-decoration: none;" v-if="mobile">
                <i title="Terms" class="fal fa-file-signature fa-lg"></i>
              </p>
              <p class="step-title" v-else>TERMS</p>
            </div>
          </div>
          <div class="steps-content">
            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Please complete your business credit application
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="is-button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="currentCustomer" @submit.prevent="validateBeforeSubmit" class="has-text-centered is-active" data-vv-scope="step1">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Business Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step1.companyName" v-validate="{ 'required': true, 'max': 25 }" :class="{ 'input': true, 'is-danger': errors.has('step1.companyName') }" data-vv-scope="step1" data-vv-as="business name" type="text" placeholder="Business Name" v-model="currentCustomer.companyName">
                          <span v-show="errors.has('step1.companyName')" class="help is-danger">
                            <i v-show="errors.has('step1.companyName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.companyName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Business Tax Id<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step1.businessTaxId" v-validate="{ 'required': true, 'min': 10, 'max': 10 }" :class="{ 'input': true, 'is-danger': errors.has('step1.businessTaxId') }" data-vv-scope="step1" data-vv-as="business tax id" type="text" placeholder="Business Tax Id" maxlength="10" v-mask="'##-#######'" v-model="currentCustomer.businessTaxId">
                          <span v-show="errors.has('step1.businessTaxId')" class="help is-danger">
                            <i v-show="errors.has('step1.businessTaxId')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.businessTaxId') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date Established<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step1.businessEstablishedDate"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.businessEstablishedDate') }"
                            data-vv-scope="step1"
                            data-vv-as="business established date"
                            size="default"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.businessEstablishedDate"
                            :picker-options="noFutureDatePickerOptions"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step1.businessEstablishedDate')" class="help is-danger">
                            <i v-show="errors.has('step1.businessEstablishedDate')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.businessEstablishedDate') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Business Type<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.businessType"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.businessType') }"
                            data-vv-scope="step1"
                            data-vv-as="business type"
                            placeholder="Select Business Type"
                            label="name"
                            track-by="id"
                            :options="definedTypes.businessType.options"
                            :searchable="true"
                            v-model="selectedBusinessType">
                          </multiselect>
                          <span v-show="errors.has('step1.businessType')" class="help is-danger">
                            <i v-show="errors.has('step1.businessType')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.businessType') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <!-- <div class="column is-6">
                      <div class="field">
                        <label class="label">Financial Statement Type<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.financialStatementType"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.financialStatementType') }"
                            data-vv-scope="step1"
                            data-vv-as="financial statement type"
                            placeholder="Select Financial Statement Type"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.financialStatementType.options"
                            :searchable="true"
                            v-model="selectedFinancialStatementType">
                          </multiselect>
                          <span v-show="errors.has('step1.financialStatementType')" class="help is-danger">
                            <i v-show="errors.has('step1.financialStatementType')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.financialStatementType') }}
                          </span>
                        </div>
                      </div>
                    </div> -->
                    <!-- <div class="column is-6">
                      <div class="field">
                        <label class="label">Finance Method<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.financeMethod"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.financeMethod') }"
                            data-vv-scope="step1"
                            data-vv-as="finance method"
                            placeholder="Select Finance Method"
                            label="name"
                            track-by="id"
                            :options="definedTypes.financeMethodType.options"
                            :searchable="true"
                            v-model="selectedFinanceMethodType">
                          </multiselect>
                          <span v-show="errors.has('step1.financeMethod')" class="help is-danger">
                            <i v-show="errors.has('step1.financeMethod')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.financeMethod') }}
                          </span>
                        </div>
                      </div>
                    </div> -->
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="step1.email" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step1.email') }" data-vv-scope="step1" data-vv-as="email" type="email" placeholder="Email" v-model="currentCustomer.email">
                          <span v-show="errors.has('step1.email')" class="help is-danger">
                            <i v-show="errors.has('step1.email')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.email') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">State of Incorporation<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step1.stateOfIncorporation"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step1.stateOfIncorporation') }"
                            data-vv-scope="step1"
                            data-vv-as="state"
                            placeholder="Select State of Incorporation"
                            label="name"
                            track-by="id"
                            :options="stateOptions"
                            :searchable="true"
                            v-model="selectedStateOfIncorporation">
                          </multiselect>
                          <span v-show="errors.has('step1.stateOfIncorporation')" class="help is-danger">
                            <i v-show="errors.has('step1.stateOfIncorporation')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.stateOfIncorporation') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Office Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="step1.office" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step1.office') }" data-vv-scope="step1" data-vv-as="office phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.office">
                          <span v-show="errors.has('step1.office')" class="help is-danger">
                            <i v-show="errors.has('step1.office')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.office') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="step1.mobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step1.mobile') }" data-vv-scope="step1" data-vv-as="mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.mobile">
                          <span v-show="errors.has('step1.mobile')" class="help is-danger">
                            <i v-show="errors.has('step1.mobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step1.mobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <!-- <div class="column is-6">
                      <div class="field">
                        <label class="label">Years in Business</label>
                        <div class="control">
                          <input name="step1.yearsInBusiness" :class="{ 'input': true, 'is-danger': errors.has('step1.yearsInBusiness') }" type="number" placeholder="Years" v-mask="'##'" v-model="currentCustomer.yearsInBusiness">
                        </div>
                      </div>
                    </div> -->
                    <!-- <div class="column is-6">
                      <div class="field">
                        <label class="label">Number of Employees</label>
                        <div class="control">
                          <input name="step1.numberOfEmployees" :class="{ 'input': true, 'is-danger': errors.has('step1.numberOfEmployees') }" type="number" placeholder="Number of Employees" v-mask="'##'" v-model="currentCustomer.numberOfEmployees">
                        </div>
                      </div>
                    </div> -->
                  </div>
                </section>
              </form>
              <form v-if="isOfficePhoneAreaCodeValid.errorCode === 0" class="has-text-centered" data-vv-scope="step1">
                <section class="has-text-left">
                  <div v-show="errors.has('step1.officeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="officeAreaCode" name="step1.officeAreaCode" id="officeAreaCode" v-validate="'required|min_value:1'" v-model="isOfficePhoneAreaCodeValid.errorCode" data-vv-scope="step1"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isOfficePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
              <form v-show="isBusinessEstablishedDateValid.errorCode === 0" class="has-text-centered" data-vv-scope="step1">
                <section class="has-text-left">
                  <div v-show="errors.has('step1.businessEstablishedDateNotFuture')" class="notification is-warning">
                    <input type="hidden" data-vv-as="businessEstablishedDateNotFuture" name="step1.businessEstablishedDateNotFuture" id="businessEstablishedDateNotFuture" v-validate="'required|min_value:1'" v-model="isBusinessEstablishedDateValid.errorCode" data-vv-scope="step1"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isBusinessEstablishedDateValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide Your Business Address
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="is-button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form class="has-text-centered" data-vv-scope="step2">
                <section class="has-text-left">
                  <address-capture v-model="address" :verification="true" :useValidator="thisValidator" scope="step2" :required="true"></address-capture>
                </section>
              </form>
            </div>

            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Please complete your business credit application
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="is-button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form @submit.prevent="validateBeforeSubmit" class="has-text-centered is-active" data-vv-scope="step3">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step3.firstName" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step3.firstName') }" data-vv-scope="step3" data-vv-as="first name" type="text" placeholder="First Name" v-model="currentCustomer.firstName">
                          <span v-show="errors.has('step3.firstName')" class="help is-danger">
                            <i v-show="errors.has('step3.firstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step3.firstName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step3.lastName" v-validate="{ 'required': true, 'max': 25 }" :class="{ 'input': true, 'is-danger': errors.has('step3.lastName') }" data-vv-scope="step3" data-vv-as="last name" type="text" placeholder="Last Name" v-model="currentCustomer.lastName">
                          <span v-show="errors.has('step3.lastName')" class="help is-danger">
                            <i v-show="errors.has('step3.lastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step3.lastName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step3.dateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step3.dateOfBirth') }"
                            data-vv-scope="step3"
                            data-vv-as="date of birth"
                            size="default"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step3.dateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step3.dateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step3.dateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field" v-if="currentSsn && currentSsn !== ''">
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input v-if="canEditSsn"
                            name="step3.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11, 'valid_ssn': true }"
                            :class="{ 'input': true, 'is-danger': errors.has('step3.ssn') }"
                            data-vv-scope="step3"
                            type="text"
                            v-model="currentCustomer.ssn"
                            v-mask="'###-##-####'">
                          <span v-else class="field is-size-7">{{ currentSsn }}</span>
                          <span v-show="errors.has('step3.ssn')" class="help is-danger">
                            <i v-show="errors.has('step3.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step3.ssn') }}
                          </span>
                        </div>
                        <div class="control is-size-7">
                          <b-checkbox
                            :native-value="false"
                            name="chkEditSsn"
                            type="is-info"
                            v-model="canEditSsn">
                            Edit SSN
                          </b-checkbox>
                        </div>
                      </div>
                      <div class="field" v-else>
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input
                            name="step3.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11, 'valid_ssn': true }"
                            :class="{ 'input': true, 'is-danger': errors.has('step3.ssn') }"
                            data-vv-scope="step3"
                            type="text"
                            v-model="currentCustomer.ssn"
                            placeholder="000-00-0000"
                            v-mask="'###-##-####'">
                          <span v-show="errors.has('step3.ssn')" class="help is-danger">
                            <i v-show="errors.has('step3.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step3.ssn') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Employment Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step3.employmentStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step3.employmentStatus') }"
                            data-vv-scope="step3"
                            data-vv-as="employment status"
                            placeholder="Select Employment Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.employmentStatus.options"
                            :searchable="true"
                            v-model="selectedEmploymentStatus">
                          </multiselect>
                          <span v-show="errors.has('step3.employmentStatus')" class="help is-danger">
                            <i v-show="errors.has('step3.employmentStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step3.employmentStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Marital Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step3.maritalStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step3.maritalStatus') }"
                            data-vv-scope="step3"
                            data-vv-as="marital status"
                            placeholder="Select Marital Status"
                            label="name"
                            track-by="id"
                            :options="definedTypes.customerMaritalStatus.options"
                            :searchable="true"
                            v-model="selectedMaritalStatus">
                          </multiselect>
                          <span v-show="errors.has('step3.maritalStatus')" class="help is-danger">
                            <i v-show="errors.has('step3.maritalStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step3.maritalStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide 3 Years of Residence History - Most recent first
                  </div>
                  <div class="column is-narrow">
                    <b-button type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form class="has-text-centered" data-vv-scope="step4">
                <div v-show="errors.has('step4.primaryAddress')" class="notification is-warning">
                    <input type="hidden" data-vv-as="primaryAddress" name="step4.primaryAddress" id="primaryAddress" v-validate="'required|min_value:1'" v-model="isPrimaryAddressValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isPrimaryAddressValid.errorMessage }}
                </div>
                <section class="has-text-left">
                  <div v-for="(address, index) in addresses" :key="address.id">
                    <address-capture v-model="addresses[index]" :addressIndex="index" :useValidator="thisValidator" :originalAddressList="previousCustomerAddresses" v-on:primary-address-set="onAutoSelectPrimaryAddress" v-on:unselect-other="onUnselectNonPrimary" v-on:on-remove-address="removeAddress" scope="step4" :required="true"></address-capture>
                    <br />
                  </div>
                  <div v-show="errors.has('step4.residenceTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="residenceTotalMonths" name="step4.residenceTotalMonths" id="residenceTotalMonths" v-validate="'required|min_value:1'" v-model="isResidenceMonthsValid.errorCode" data-vv-scope="step4"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isResidenceMonthsValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons" type="is-info" inverted @click="addAddress({})">Add</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showWorkStep" class="step-content">
              <div v-if="showWorkStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    {{ workHistoryHeading }} - {{ workHistorySubHeading }}
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" type="button" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showWorkStep" class="has-text-centered" data-vv-scope="step5">
                <section class="has-text-left">
                  <div v-for="(history, index) in workHistory" :key="history.id">
                    <work-history-capture v-model="workHistory[index]" :employmentStatusType="selectedEmploymentStatus ? selectedEmploymentStatus.id : 0" :popFillData="address" :scope="'step5'" :required="true"></work-history-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step5.workHistoryTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryTotalMonths" name="step5.workHistoryTotalMonths" id="workHistoryTotalMonths" v-validate="'required|min_value:1'" v-model="isWorkHistoryMonthsValid.errorCode" data-vv-scope="step5"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryMonthsValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step5.workHistoryEmployerName')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryEmployerName" name="step5.workHistoryEmployerName" id="workHistoryEmployerName" v-validate="'required|min_value:1'" v-model="isWorkHistoryEmployerValid.errorCode" data-vv-scope="step5"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryEmployerValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step5.workHistoryOfficeNumber')" class="notification is-warning">
                    <input type="hidden" data-vv-as="workHistoryOfficeNumber" name="step5.workHistoryOfficeNumber" id="workHistoryOfficeNumber" v-validate="'required|min_value:1'" v-model="isWorkHistoryPhoneAreaCodeValid.errorCode" data-vv-scope="step5"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isWorkHistoryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addWorkHistory({})">Add</b-button>
                    <b-button icon-left="fa fa-lg fa-minus-circle" class="customButtons is-danger" type="button" inverted v-if="showRemoveWorkHistory" @click="removeWorkHistory({})">Remove</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseStep" class="step-content">
              <div v-if="showSpouseStep" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide Spouse Details
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="is-button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseStep" class="has-text-centered" data-vv-scope="step6">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step6.firstName" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step6.firstName') }" data-vv-scope="step6" data-vv-as="first name" type="text" placeholder="First Name" v-model="currentCustomer.spouse.firstName">
                          <span v-show="errors.has('step6.firstName')" class="help is-danger">
                            <i v-show="errors.has('step6.firstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.firstName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control capitilized">
                          <input name="step6.lastName" v-validate="{ 'required': true, 'max':25 }" :class="{ 'input': true, 'is-danger': errors.has('step6.lastName') }" data-vv-scope="step6" data-vv-as="last name" type="text" placeholder="Last Name" v-model="currentCustomer.spouse.lastName">
                          <span v-show="errors.has('step6.lastName')" class="help is-danger">
                            <i v-show="errors.has('step6.lastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.lastName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Date of Birth<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <el-date-picker
                            name="step6.sDateOfBirth"
                            v-validate="{ 'required': true, 'minimum_age_18': true }"
                            :class="{ 'is-danger': errors.has('step6.sDateOfBirth') }"
                            data-vv-scope="step6"
                            data-vv-as="date of birth"
                            size="large"
                            prefix-icon="none"
                            type="date"
                            format="MM/dd/yyyy"
                            placeholder="Click to select..."
                            v-model="currentCustomer.spouse.dateOfBirth"
                            :picker-options="datePickerOptions"
                            :default-value="defaultDate"
                            :clearable="false"
                            style="width: 100%;">
                          </el-date-picker>
                          <span v-show="errors.has('step6.sDateOfBirth')" class="help is-danger">
                            <i v-show="errors.has('step6.sDateOfBirth')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.sDateOfBirth') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-calendar-alt default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field" v-if="currentSpouseSsn && currentSpouseSsn !== ''">
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input v-if="canEditSpouseSsn"
                            name="step6.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11, 'valid_ssn': true }"
                            :class="{ 'input': true, 'is-danger': errors.has('step6.ssn') }"
                            data-vv-scope="step6"
                            type="text"
                            v-model="currentCustomer.spouse.ssn"
                            v-mask="'###-##-####'">
                          <span v-else class="field is-size-7">{{ currentSpouseSsn }}</span>
                          <span v-show="errors.has('step6.ssn')" class="help is-danger">
                            <i v-show="errors.has('step6.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.ssn') }}
                          </span>
                        </div>
                        <div class="field is-size-7 is-pulled-left mt-10">
                          <b-checkbox
                            :native-value="false"
                            name="chkEditSsn"
                            type="is-info"
                            v-model="canEditSpouseSsn">
                            edit SSN
                          </b-checkbox>
                        </div>
                      </div>
                      <div class="field" v-else>
                        <label class="label">Social Security No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input
                            name="step6.ssn"
                            data-vv-as="social security number"
                            v-validate="{ 'required': true, 'min': 11, 'max': 11, 'valid_ssn': true }"
                            :class="{ 'input': true, 'is-danger': errors.has('step6.ssn') }"
                            data-vv-scope="step6"
                            type="text"
                            v-model="currentCustomer.spouse.ssn"
                            placeholder="000-00-0000"
                            v-mask="'###-##-####'">
                          <span v-show="errors.has('step6.ssn')" class="help is-danger">
                            <i v-show="errors.has('step6.ssn')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.ssn') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Email<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="email" v-validate="{ 'required': true, 'email': true, 'max': 50 }" :class="{ 'input': true, 'is-danger': errors.has('step6.email') }" data-vv-scope="step6" data-vv-as="email" type="email" placeholder="Email" v-model="currentCustomer.spouse.email">
                          <span v-show="errors.has('step6.email')" class="help is-danger">
                            <i v-show="errors.has('step6.email')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.email') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-envelope default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Home Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="home" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step6.home') }" data-vv-scope="step6" data-vv-as="home phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.spouse.home">
                          <span v-show="errors.has('step6.home')" class="help is-danger">
                            <i v-show="errors.has('step6.home')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.home') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Mobile Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="mobile" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step6.mobile') }" data-vv-scope="step6" data-vv-as="mobile phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.spouse.mobile">
                          <span v-show="errors.has('step6.mobile')" class="help is-danger">
                            <i v-show="errors.has('step6.mobile')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.mobile') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-mobile-android default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Employment Status<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <multiselect
                            :multiple="false"
                            name="step6.employmentStatus"
                            v-validate="{ 'required': true }"
                            :class="{ 'is-danger': errors.has('step6.employmentStatus') }"
                            data-vv-scope="step6"
                            data-vv-as="employment status"
                            placeholder="Select Employment Status"
                            label="altName"
                            track-by="id"
                            :options="definedTypes.employmentStatus.options"
                            :searchable="true"
                            v-model="selectedSpouseEmploymentStatus">
                          </multiselect>
                          <span v-show="errors.has('step6.employmentStatus')" class="help is-danger">
                            <i v-show="errors.has('step6.employmentStatus')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step6.employmentStatus') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
              <form v-if="showSpouseStep && (isSpousePhoneAreaCodeValid.errorCode === 0 || isSpouseMobilePhoneAreaCodeValid.errorCode === 0)" class="has-text-centered" data-vv-scope="step6">
                <section class="has-text-left">
                  <div v-show="errors.has('step6.spouseHomeAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseHomeAreaCode" name="step6.spouseHomeAreaCode" id="spouseHomeAreaCode" v-validate="'required|min_value:1'" v-model="isSpousePhoneAreaCodeValid.errorCode" data-vv-scope="step6"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpousePhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step6.spouseMobileAreaCode')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseMobileAreaCode" name="step6.spouseMobileAreaCode" id="spouseMobileAreaCode" v-validate="'required|min_value:1'" v-model="isSpouseMobilePhoneAreaCodeValid.errorCode" data-vv-scope="step6"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseMobilePhoneAreaCodeValid.errorMessage }}
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseResidence" class="step-content">
              <div v-if="showSpouseResidence" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide 3 Years of Residence History - Most recent first
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="is-button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseResidence" class="has-text-centered" data-vv-scope="step7">
                <div v-show="errors.has('step7.spousePrimaryAddress')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spousePrimaryAddress" name="step7.spousePrimaryAddress" id="spousePrimaryAddress" v-validate="'required|min_value:1'" v-model="isSpousePrimaryAddressValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpousePrimaryAddressValid.errorMessage }}
                </div>
                <section class="has-text-left">
                  <div v-for="(address, index) in spouseAddresses" :key="address.id">
                    <address-capture v-model="spouseAddresses[index]" :useValidator="thisValidator" :isSpouse="true" v-on:primary-address-set="onAutoSelectSpousePrimaryAddress" v-on:unselect-other="onUnselectSpouseNonPrimary" v-on:on-remove-address="removeAddress" :addressIndex="index" scope="step7" :popFillData="primaryAddress" :required="true"></address-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step7.spouseResidenceTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseResidenceTotalMonths" name="step7.spouseResidenceTotalMonths" id="spouseResidenceTotalMonths" v-validate="'required|min_value:1'" v-model="isSpouseResidenceMonthsValid.errorCode" data-vv-scope="step7"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseResidenceMonthsValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addAddress({ spouseAddress: true })">Add</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div v-show="showSpouseWork" class="step-content">
              <div v-if="showSpouseWork" class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    {{ spouseWorkHistoryHeading }} - {{ spouseWorkHistorySubHeading }}
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="is-button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form v-if="showSpouseWork" class="has-text-centered" data-vv-scope="step8">
                <section class="has-text-left">
                  <div v-for="(history, index) in spouseWorkHistory" :key="history.id">
                    <work-history-capture v-model="spouseWorkHistory[index]" :employmentStatusType="selectedSpouseEmploymentStatus ? selectedSpouseEmploymentStatus.id : 0" scope="step8" :required="true"></work-history-capture>
                    <br/>
                  </div>
                  <div v-show="errors.has('step8.spouseWorkHistoryTotalMonths')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryTotalMonths" name="step8.spouseWorkHistoryTotalMonths" id="spouseWorkHistoryTotalMonths" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryMonthsValid.errorCode" data-vv-scope="step8"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryMonthsValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step8.spouseWorkHistoryEmployerName')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryEmployerName" name="step8.spouseWorkHistoryEmployerName" id="spouseWorkHistoryEmployerName" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryEmployerValid.errorCode" data-vv-scope="step8"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryEmployerValid.errorMessage }}
                  </div>
                  <div v-show="errors.has('step8.spouseWorkHistoryOfficeNumber')" class="notification is-warning">
                    <input type="hidden" data-vv-as="spouseWorkHistoryOfficeNumber" name="step8.spouseWorkHistoryOfficeNumber" id="spouseWorkHistoryOfficeNumber" v-validate="'required|min_value:1'" v-model="isSpouseWorkHistoryPhoneAreaCodeValid.errorCode" data-vv-scope="step8"/>
                    <i class="fas fa-exclamation-triangle"></i>{{ isSpouseWorkHistoryPhoneAreaCodeValid.errorMessage }}
                  </div>
                  <div class="inner-add-remove-button customButtonContainer" v-if="!creditApplicationCompleted">
                    <b-button icon-left="fa fa-lg fa-plus-circle" class="customButtons is-info" type="button" inverted @click="addWorkHistory({spouseWorkHistory: true})">Add</b-button>
                    <b-button icon-left="fa fa-lg fa-minus-circle" class="customButtons is-danger" type="button" inverted v-if="showRemoveSpouseWorkHistory" @click="removeWorkHistory({spouseWorkHistory: true})">Remove</b-button>
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content">
              <div class="credit-app has-background-grey-dark has-text-white">
                <div class="columns">
                  <div class="column">
                    Provide Your Banking Info
                  </div>
                  <div class="column is-narrow">
                    <b-button v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right ml-10' : 'mb-5'" class="is-info is-light" type="is-button" @click="saveAndContinueLater">Save and continue later</b-button>
                    <b-button v-if="!creditApplicationCompleted" :class="!isMobile ? 'is-pulled-right' : ''" class="is-info is-light" type="button" @click="navigateToCustomer">Switch to individual app</b-button>
                  </div>
                </div>
              </div>
              <form class="has-text-centered" data-vv-scope="step9">
                <section class="has-text-left box">
                  <div class="columns is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Bank Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step9.businessBankName" v-validate="{ 'required': true, 'max': 20 }" :class="{ 'input': true, 'is-danger': errors.has('step9.businessBankName') }" data-vv-scope="step9" data-vv-as="bank name" type="text" placeholder="Bank Name" v-model="currentCustomer.businessBankName">
                          <span v-show="errors.has('step9.businessBankName')" class="help is-danger">
                            <i v-show="errors.has('step9.businessBankName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step9.businessBankName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Bank Account No.<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step9.businessBankAccountNo" v-validate="{ 'required': true, 'max': 15 }" :class="{ 'input': true, 'is-danger': errors.has('step9.businessBankAccountNo') }" data-vv-scope="step9" data-vv-as="bank account no." type="text" placeholder="Bank Account No." maxlength="15" v-model="currentCustomer.businessBankAccountNo">
                          <span v-show="errors.has('step9.businessBankAccountNo')" class="help is-danger">
                            <i v-show="errors.has('step9.businessBankAccountNo')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step9.businessBankAccountNo') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Bank Contact Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control">
                          <input name="step9.businessBankContactName" v-validate="{ 'required': true, 'max': 30 }" :class="{ 'input': true, 'is-danger': errors.has('step9.businessBankContactName') }" data-vv-scope="step9" data-vv-as="bank contact name" type="text" placeholder="Bank Contact Name" v-model="currentCustomer.businessBankContactName">
                          <span v-show="errors.has('step9.businessBankContactName')" class="help is-danger">
                            <i v-show="errors.has('step9.businessBankContactName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step9.businessBankContactName') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Bank Contact Phone<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-right">
                          <input name="step9.businessBankContactPhone" v-validate="{ 'required': true, 'min': 16, 'max': 16 }" :class="{ 'input': true, 'is-danger': errors.has('step9.businessBankContactPhone') }" data-vv-scope="step9" data-vv-as="bank contact phone" type="tel" placeholder="+1(999)-999-9999" v-mask="'+1(###)-###-####'" v-model="currentCustomer.businessBankContactPhone">
                          <span v-show="errors.has('step9.businessBankContactPhone')" class="help is-danger">
                            <i v-show="errors.has('step9.businessBankContactPhone')" class="fas fa-exclamation-triangle"></i>{{ errors.first('step9.businessBankContactPhone') }}
                          </span>
                          <span class="icon is-small is-right">
                            <i class="fal fa-phone-volume default-blue"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </form>
            </div>

            <div class="step-content has-text-centered">
              <div class="box" v-observe-visibility="visibilityChanged">
                <div class="field notification is-aligned-left ml-20">
                  <terms-control ref="termsControl" :currentCustomer="currentCustomer"></terms-control>
                </div>
                <div class="field notification is-aligned-left ml-20" v-if="communityPropertyDisclosureRequired">
                  <property-disclosure-control ref="copControl" :currentCustomer="currentCustomer"></property-disclosure-control>
                </div>
              </div>
            </div>
          </div>

          <div class="steps-actions">
            <div class="steps-action">
              <b-button icon-left="chevron-left" data-nav="previous" class="is-info" :disabled="isSaving">Previous</b-button>
            </div>
            <div class="steps-action">
              <b-button icon-right="chevron-right" data-nav="next" class="next-step is-info" @click="validateStep">Next</b-button>
            </div>
            <div class="steps-action" v-if="termsVisible && !creditApplicationCompleted">
              <a class="button next-step is-success" @click="validateBeforeSubmitAllScoped" :class="{'is-loading': isSaving}" :disabled="isSaving">Submit</a>
            </div>
          </div>
          <div class="container" v-if="isValidToken && isCompanyNameValid && isEmailValid && !creditApplicationCompleted">
            <b-button  type="button" class="is-info is-light" @click="navigateToCustomer">Switch to individual app</b-button>
            <br />
            <br />
            <b-button type="button" class="is-info is-light" @click="saveAndContinueLater" :disabled="isSaving">Save and continue later</b-button>
          </div>
        </div>
      </div>
    </div>
    <div v-show="!isValidToken && isLoading" class="hero">
      <div class="hero-body container">
        <div class="container is-fluid">
          <div class="columns is-vcentered loadingContainer">
            <div class="column is-12 has-text-centered has-text-weight-semi-bold">
              <img src="@/assets/ajax-loader.gif">
              <div v-if="!loadingBaseData">Verifying your identity....</div>
              <div v-if="loadingBaseData">Loading base data....</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-show="!isValidToken && !isLoading" class= "hero">
      <div class="hero-body container"></div>
    </div>
    <location-control v-model="isLocationModalActive"></location-control>
  </section>
</template>

<script>
import moment from 'moment'
import customerService from '@/services/customerService'
import customerMixin from '@/mixins/businessCustomer/index'
import utilitiesMixin from '@/mixins/generic/utilities'
import LocationControl from '@/components/generic/customer/LocationControl'
import bulmaSteps from 'bulma-steps/src/js/bulma-steps'

export default {
  name: 'BusinessCreditApplication',
  inject: {
    $validator: '$validator'
  },
  props: {
    token: {
      type: String,
      default: ''
    }
  },
  components: {
    'location-control': LocationControl
  },
  mixins: [customerMixin, utilitiesMixin],
  data () {
    return {
      isMobile: window.innerWidth <= 700,
      isLocationModalActive: false,
      isValidToken: false,
      reg: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
      currentUrl: '',
      geoLocationEnabled: false,
      locationModalShown: false,
      currentCustomer: {
        id: 0,
        dealerId: 0,
        type: 'Company',
        status: 'Active',
        companyName: null,
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        ssn: '',
        email: null,
        mobile: null,
        office: null,
        isPartial: false,
        isCreditTermsAccepted: false,
        isCreditApplicationComplete: false,
        businessTaxId: '',
        financialStatementType: null,
        businessType: null,
        financeMethod: null,
        stateOfIncorporation: null,
        businessEstablishedDate: null,
        yearsInBusiness: null,
        numberOfEmployees: null,
        addresses: [],
        businessAddress1: '',
        businessAddress2: '',
        businessCity: '',
        businessState: '',
        businessCountry: '',
        businessPostalCode: '',
        businessBankName: '',
        businessBankAccountNo: '',
        businessBankContactName: '',
        businessBankContactPhone: '',
        isContinueLater: false,
        url: '',
        leadId: '00000000-0000-0000-0000-000000000000',
        agentUserId: '00000000-0000-0000-0000-000000000000',
        agentLocationId: 0,
        make: 0,
        makeName: null,
        model: null,
        year: null,
        vinNo: null,
        createdAt: null,
        modifiedAt: null
      },
      address: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        companyName: ''
      },
      canEditSsn: false,
      currentSsn: '',
      currentSpouseSsn: '',
      canEditSpouseSsn: false
    }
  },
  computed: {
    isCompanyNameValid: function () {
      return this.currentCustomer && (this.currentCustomer.companyName && this.currentCustomer.companyName !== '')
    },
    isEmailValid: function () {
      return this.currentCustomer && (this.currentCustomer.email && this.currentCustomer.email !== '') && this.reg.test(this.currentCustomer.email)
    },
    availableScopes: function () {
      return ['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7', 'step8', 'step9', 'step10']
    }
  },
  methods: {
    fetchCreditApplication: async function (currentToken) {
      this.serverErrorMessage = ''
      this.loadingBaseData = true
      this.isLoading = true

      customerService.getBusinessCreditApplication(currentToken).then(response => {
        this.currentCustomer = response.customer
        this.initInputValues()
        this.isLoading = false
        this.isValidToken = true
        if (this.currentCustomer.userId && this.currentCustomer.userId > 0) {
          this.$store.dispatch('user/setCurrentUser', this.currentCustomer.userId)
        }
        if (this.currentCustomer.isCreditApplicationCancelled) {
          this.$router.push({ name: 'CreditApplicationCancelled' })
        } else {
          this.dealerId = this.currentCustomer.dealerId
          if (!this.dealerId || this.dealerId === 0) {
            this.serverErrorMessage = 'Could not load dealer information for customer'
          }
        }
      // eslint-disable-next-line handle-callback-err
      }).catch((error) => {
        this.isLoading = false
        this.serverErrorMessage = 'Could not load customer information with code: ' + currentToken
      })
    },
    fetchCustomer: function (id) {
      this.isLoading = true
      customerService.single(id).then(response => {
        this.isLoading = false
        this.currentCustomer = response.customer
        this.initInputValues()
        this.isValidToken = true
        if (this.currentCustomer.isCreditApplicationCancelled) {
          this.$router.push({ name: 'CreditApplicationCancelled' })
        } else {
          this.dealerId = this.currentCustomer.dealerId
          if (!this.dealerId || this.dealerId === 0) {
            this.serverErrorMessage = 'Could not load dealer information for customer'
          }
        }
      }).catch((error) => {
        this.isLoading = false
        this.serverErrorMessage = error.message
      })
    },
    initInputValues () {
      if (this.currentCustomer != null) {
        this.address = {
          address1: this.currentCustomer.businessAddress1,
          address2: this.currentCustomer.businessAddress2,
          city: this.currentCustomer.businessCity,
          country: this.currentCustomer.businessCountry,
          state: this.currentCustomer.businessState && this.currentCustomer.businessState !== ''
            ? this.definedTypes.usStateLookup.options.find((x) => x.abbreviation === this.currentCustomer.businessState || x.name === this.currentCustomer.businessState).name
            : null,
          postalCode: this.currentCustomer.businessPostalCode,
          companyName: this.currentCustomer.companyName
        }

        this.previousCustomerAddresses = []
        this.currentCustomer.addresses.forEach(function (address) {
          var config = {
            sectionId: this.addresses.length + 100,
            loadCustomerOnly: false
          }
          address.config = config
          const checkDate = moment.tz(new Date(), moment.tz.guess()).subtract(10, 'day').format('YYYY-MM-DD')
          const currentDate = moment.tz(new Date(), moment.tz.guess()).format('YYYY-MM-DD')
          const modifiedDate = moment(new Date(address.modifiedAt)).format('YYYY-MM-DD')
          if (modifiedDate >= checkDate && modifiedDate <= currentDate) {
            this.addresses.push(address)
          } else {
            this.previousCustomerAddresses.push(address)
          }
        }, this)
        this.workHistory = []
        this.currentCustomer.workHistory.forEach(function (history) {
          var config = {
            sectionId: this.workHistory.length + 100,
            loadCustomerOnly: false
          }
          history.config = config
          this.workHistory.push(history)
        }, this)
        if (this.addresses.length === 0) {
          this.addAddress({})
        }
        if (this.workHistory.length === 0) {
          this.addWorkHistory()
        }

        this.selectedBusinessType = this.currentCustomer.businessType
          ? this.definedTypes.businessType.options.find((x) => x.name === this.currentCustomer.businessType)
          : null
        this.selectedStateOfIncorporation = this.currentCustomer.stateOfIncorporation
          ? this.definedTypes.usStateLookup.options.find((x) => x.abbreviation === this.currentCustomer.stateOfIncorporation)
          : null
        this.selectedMaritalStatus = this.currentCustomer.maritalStatus
          ? this.definedTypes.customerMaritalStatus.options.find((x) => x.name === this.currentCustomer.maritalStatus)
          : this.definedTypes.customerMaritalStatus.options.find((x) => x.id === 0)
        this.selectedEmploymentStatus = this.currentCustomer.employmentStatus
          ? this.definedTypes.employmentStatus.options.find((x) => x.name === this.currentCustomer.employmentStatus)
          : this.definedTypes.employmentStatus.options.find((x) => x.id === 1)

        if (this.currentCustomer.spouse) {
          this.spouseAddresses = []
          this.currentCustomer.spouse.addresses.forEach(function (address) {
            var config = {
              sectionId: this.spouseAddresses.length + 100,
              loadCustomerOnly: false
            }
            address.config = config
            this.spouseAddresses.push(address)
          }, this)
          this.spouseWorkHistory = []
          this.currentCustomer.spouse.workHistory.forEach(function (history) {
            var config = {
              sectionId: this.spouseWorkHistory.length + 100,
              loadCustomerOnly: false
            }
            history.config = config
            this.spouseWorkHistory.push(history)
          }, this)
          this.currentSpouseSsn = this.currentCustomer.spouse.ssn
          this.currentCustomer.spouse.ssn = ''

          const employmentStatus = this.currentCustomer.spouse.employmentStatus
          this.selectedSpouseEmploymentStatus = employmentStatus
            ? this.definedTypes.employmentStatus.options.find((x) => x.name === employmentStatus)
            : this.definedTypes.employmentStatus.options.find((x) => x.id === 1)
        }

        this.currentSsn = this.currentCustomer.ssn
        this.currentCustomer.ssn = ''
      }
    },
    onSave: function () {
      if (!this.geoLocationEnabled && !this.locationModalShown) {
        this.locationModalShown = true
        this.isLocationModalActive = true
      } else {
        this.currentCustomer.isCreditContinueLater = false
        this.save()
      }
    },
    onComplete () {
      if (this.currentCustomer.isCreditContinueLater) {
        this.$router.push({ name: 'CreditApplicationContinueLater' })
      } else {
        this.$router.push({ name: 'CreditApplicationComplete' })
      }
    },
    async saveAndContinueLater () {
      this.currentCustomer.isCreditContinueLater = true
      this.currentCustomer.url = this.currentUrl
      await this.updateReturnUrl(this.currentUrl)
      await this.updateIsContinueLater(true)
      this.save()
    },
    save () {
      if (!this.currentCustomer.isCreditTermsAccepted) {
        this.$refs.termsControl.showError()
        return
      }
      if (this.communityPropertyDisclosureRequired && !this.currentCustomer.communityPropertyDisclosure) {
        this.$refs.copControl.showError()
        return
      }
      // A disclosure left checked for a state that no longer requires it is rejected by
      // DealerTrack. The control is hidden in that case, so clear it on the model here -
      // otherwise the stale value is reposted on every save with no way to undo it.
      // Skipped on "save and continue later": that path runs no validation, so the address
      // state may be unresolved and communityPropertyDisclosureRequired untrustworthy - and
      // a partial save never reaches DealerTrack, so there is nothing to protect there.
      if (!this.currentCustomer.isCreditContinueLater && !this.communityPropertyDisclosureRequired) {
        this.currentCustomer.communityPropertyDisclosure = false
      }
      var customerModel = {
        id: this.currentCustomer.id,
        isCreate: false,
        subscriberId: this.currentCustomer.subscriberId,
        dealerId: this.dealerId,
        type: 'Company',
        companyName: this.currentCustomer.companyName,
        firstName: this.currentCustomer.firstName,
        lastName: this.currentCustomer.lastName,
        dateOfBirth: this.currentCustomer.dateOfBirth,
        ssn: this.currentCustomer.ssn !== '' ? this.currentCustomer.ssn : this.currentSsn,
        email: this.currentCustomer.email,
        mobile: this.currentCustomer.mobile,
        office: this.currentCustomer.office,
        status: this.currentCustomer.status,
        isCreditTermsAccepted: this.currentCustomer.isCreditTermsAccepted,
        businessTaxId: this.currentCustomer.businessTaxId,
        maritalStatus: this.selectedMaritalStatus ? this.selectedMaritalStatus.name : null,
        businessType: this.selectedBusinessType ? this.selectedBusinessType.name : null,
        employmentStatus: this.selectedEmploymentStatus ? this.selectedEmploymentStatus.name : null,
        stateOfIncorporation: this.selectedStateOfIncorporation ? this.selectedStateOfIncorporation.abbreviation : null,
        businessEstablishedDate: this.currentCustomer.businessEstablishedDate,
        addresses: [],
        workHistory: [],
        communityPropertyDisclosure: this.currentCustomer.communityPropertyDisclosure,
        spouseId: this.currentCustomer.spouseId,
        spouse: {},
        isSpouseCoApplicant: false,
        businessAddress1: this.address.address1,
        businessAddress2: this.address.address2,
        businessCity: this.address.city,
        businessState: this.address.state,
        businessCountry: this.address.country,
        businessPostalCode: this.address.postalCode,
        businessBankName: this.currentCustomer.businessBankName,
        businessBankAccountNo: this.currentCustomer.businessBankAccountNo,
        businessBankContactName: this.currentCustomer.businessBankContactName,
        businessBankContactPhone: this.currentCustomer.businessBankContactPhone,
        isContinueLater: this.currentCustomer.isContinueLater,
        url: this.currentCustomer.url,
        isCreditContinueLater: this.currentCustomer.isCreditContinueLater,
        latestCreditApplicationId: this.currentCustomer.latestCreditApplicationId,
        customerId: null,
        leadId: null,
        agentUserId: null,
        agentLocationId: 0,
        userId: this.currentCustomer.userId
      }

      this.saveAddress(customerModel)
      this.saveWorkHistory(customerModel)

      if (this.spouseInfoRequired) {
        var spouseCustomerModel = {
          id: this.currentCustomer.spouse.id,
          isCreate: false,
          subscriberId: this.currentCustomer.spouse.subscriberId,
          dealerId: this.dealerId,
          type: this.currentCustomer.spouse.type,
          firstName: this.$lodash.startCase(this.currentCustomer.spouse.firstName),
          lastName: this.$lodash.startCase(this.currentCustomer.spouse.lastName),
          email: this.currentCustomer.spouse.email,
          mobile: this.currentCustomer.spouse.mobile,
          home: this.currentCustomer.spouse.home,
          addresses: [],
          workHistory: [],
          status: this.currentCustomer.spouse.status,
          ssn: this.currentCustomer.spouse.ssn !== '' ? this.currentCustomer.spouse.ssn : this.currentSpouseSsn,
          dateOfBirth: moment(this.currentCustomer.spouse.dateOfBirth).format('MM/DD/YYYY'),
          defaultPaymentMethod: this.currentCustomer.spouse.defaultPaymentMethod,
          maritalStatus: this.currentCustomer.selectedMaritalStatus ? this.currentCustomer.selectedMaritalStatus.name : null,
          employmentStatus: this.selectedSpouseEmploymentStatus.name,
          isContinueLater: this.currentCustomer.spouse.isContinueLater,
          isCreditContinueLater: this.currentCustomer.spouse.isCreditContinueLater,
          ageInMonths: this.calculateSpouseAgeInMonths,
          userId: this.currentCustomer.userId
        }
        customerModel.spouse = spouseCustomerModel
        this.saveSpouseAddress(customerModel.spouse)
        this.saveSpouseWorkHistory(customerModel.spouse)
      }

      customerModel.customerId = this.currentCustomer.customerId
      customerModel.leadId = this.currentCustomer.leadId
      customerModel.agentUserId = this.currentCustomer.agentUserId
      customerModel.agentLocationId = this.currentCustomer.agentLocationId

      this.isSaving = true
      this.serverErrorMessage = ''
      this.serverErrors = []
      customerService.updateBusiness(customerModel).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
          this.$nextTick(function () {
            var elmnt = document.getElementById('main-page')
            elmnt.scrollTo(0, 0)
          })
        } else {
          this.$emit('on-save', response.customer)
          this.onChangesSaved()
          this.onComplete()
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
        this.$nextTick(function () {
          var elmnt = document.getElementById('main-page')
          elmnt.scrollTo(0, 0)
        })
      })
    },
    async navigateToCustomer () {
      this.$router.push({ name: 'CreditApplication', query: { token: this.token } })
    }
  },
  created () {
    this.currentUrl = window.location.href
    if (!('geolocation' in navigator)) {
      this.geoLocationEnabled = false
    } else {
      this.geoLocationEnabled = true
    }
    this.$emit('is-home', false)
  },
  watch: {
    token: function (newValue, oldValue) {
      this.isValidToken = false
    },
    'currentCustomer.companyName': function (newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.address.companyName = this.currentCustomer.companyName
      }
    }
  },
  mounted () {
    this.bulmaSteps = bulmaSteps.attach('.steps', {
      stepClickable: this.isUser || this.isMember,
      onValidateStep: (a, b, e) => {
        this.validateStep(e)
      },
      beforeNext: id => {
        var beforeNextResult = {}
        if (this.preventClick) {
          beforeNextResult.errors = ['Validation Failed']
          return beforeNextResult
        } else {
          switch (this.currentStep) {
            case 3:
              beforeNextResult.errors = this.validateResidenceHistory()
              if (beforeNextResult.errors.length === 0 && !this.showWorkStep) {
                if (!this.showSpouseStep) {
                  beforeNextResult.skipCount = 4
                } else {
                  beforeNextResult.skipCount = 1
                }
              }
              return beforeNextResult
            case 4:
              beforeNextResult.errors = this.validateWorkHistory()
              if (beforeNextResult.errors.length === 0 && this.showWorkStep) {
                if (!this.showSpouseStep) {
                  beforeNextResult.skipCount = 3
                }
              }
              return beforeNextResult
            case 6:
              beforeNextResult.errors = this.validateSpouseResidenceHistory()
              if (beforeNextResult.errors.length === 0 && !this.showSpouseWork) {
                if (this.showSpouseStep) {
                  beforeNextResult.skipCount = 1
                }
              }
              return beforeNextResult
            case 7:
              beforeNextResult.errors = this.validateSpouseWorkHistory()
              return beforeNextResult
          }
          return beforeNextResult
        }
      },
      beforePrevious: id => {
        this.currentStep = id
        let skipCount = 0
        switch (this.currentStep) {
          case 4:
            if (!this.showWorkStep) {
              skipCount = 1
            }
            return skipCount
          case 5:
            if (this.showSpouseStep && !this.showWorkStep) {
              skipCount = 1
            }
            return skipCount
          case 8:
            if (!this.showWorkStep) {
              if (!this.showSpouseStep) {
                skipCount = 4
              } else {
                if (!this.showSpouseWork) {
                  skipCount = 1
                }
              }
            } else {
              if (!this.showSpouseStep) {
                skipCount = 3
              } else {
                if (!this.showSpouseWork) {
                  skipCount = 1
                }
              }
            }
            return skipCount
        }
      },
      onShow: id => {
        var elmnt = document.getElementById('main-page')
        elmnt.scrollTo(0, 0)
        this.currentStep = id
      }
    })
    this.currentCustomerId = this.$route.params.id
    if (this.currentCustomerId && this.currentCustomerId > 0) {
      this.fetchCustomer(this.currentCustomerId)
    } else {
      this.fetchCreditApplication(this.token)
    }
    this.$emit('set-title-one', 'BUSINESS CREDIT')
    this.$emit('set-title-two', 'APPLICATION')
  }
}

</script>

<style scoped>
  .customButtons {
    display: flex !important;
    flex-basis: 0 !important;
    flex-grow: 1 !important;
    margin: .5rem !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .customButtonContainer {
    display: flex !important;
    align-items: stretch !important;
    flex-basis: 100% !important;
  }

  .box {
    background-color: #f6f8fa;
  }

  .is-danger > .el-input__inner:hover {
    border-color: #ff0000 !important;
  }
</style>
